import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import {
  Button,
  Space,
  Col,
  Statistic,
  message,
  Typography,
  Input,
  Tag,
  ConfigProvider,
} from "antd";
import {
  ReloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { faCheckCircle, faPen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DisputesTable from "./DisputesTable";
import {
  Container,
  StyledCard,
  StatsRow,
  StatCard,
  HeaderSection,
  TopControlBar,
  StyledModal,
  ModalFooter,
  CloseButton,
  SubmitButton,
} from "./Styles";
import DisputeDetailsModal from "./DisputeDetailsModal";
import {
  fetchDisputes,
  fetchDispute,
  updateDisputeNotes,
  submitDisputeResponse,
  setSelectedDispute,
  clearSelectedDispute,
} from "./store/disputesSlice";
import type { AppDispatch, RootState } from "../../redux/store";
import type { DisputesState } from "./store/disputesSlice";
import CustomSelector from "../../commonComponents/Selector";
import CustomInput from "../../commonComponents/input";
import {
  getStatusLabel,
  getStatusColor,
  isUrgentStatus,
  canSubmitResponse,
  STATUS_FILTER_OPTIONS,
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_PAGE,
  DEFAULT_STATUS_FILTER,
} from "./constants";
import { Dispute } from "./types";
import { useTheme } from "../../context/ThemeContext";
import { getAntdThemeConfig } from "../../utils/antdTheme";

const { Title, Text } = Typography;
const { TextArea } = Input;

const DisputeManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    disputes,
    selectedDispute,
    loading,
    loadingDetails,
    updatingNotes,
    submittingResponse,
    stats,
  } = useSelector((state: RootState): DisputesState => state.disputes);

  const [filteredDisputes, setFilteredDisputes] = useState<Dispute[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_ITEMS_PER_PAGE);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>(DEFAULT_STATUS_FILTER);
  const [responseNotes, setResponseNotes] = useState<string>("");
  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);
  const [isNotesOpen, setIsNotesOpen] = useState<boolean>(false);
  const lastFetchedStatus = useRef<string | null>(null);
  const isFetching = useRef<boolean>(false);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (lastFetchedStatus.current === statusFilter || isFetching.current || loading) {
      return;
    }
    lastFetchedStatus.current = statusFilter;
    handleFetchDisputes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    let filtered = [...disputes];

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (dispute) => dispute.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (dispute) =>
          dispute.evidence?.customerName?.toLowerCase().includes(query)
      );
    }

    setFilteredDisputes(filtered);
    setCurrentPage(1);
  }, [disputes, searchQuery, statusFilter]);

  const handleFetchDisputes = async () => {
    if (isFetching.current || loading) return;    
    isFetching.current = true;
    try {
      const statusParam = statusFilter !== "all" ? statusFilter : null;
      const result = await dispatch(fetchDisputes({ merchantId: null, status: statusParam }));
      if (fetchDisputes.rejected.match(result)) {
        const errorMessage = typeof result.payload === 'string' ? result.payload : "Failed to fetch disputes. Please try again.";
        message.error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error fetching disputes:", error);
      message.error("Failed to fetch disputes. Please try again.");
    } finally {
      isFetching.current = false;
    }
  };

  const handleViewDetails = async (dispute: Dispute) => {
    dispatch(setSelectedDispute(dispute));
    setResponseNotes(dispute.responseNotes || "");
    setIsDetailOpen(true);
    
    try {
      const result = await dispatch(fetchDispute(String(dispute.id)));
      if (fetchDispute.fulfilled.match(result)) {
        setResponseNotes(result.payload?.responseNotes || "");
      } else {
        message.warning("Could not fetch full details. Showing available information.");
      }
    } catch (error: any) {
      console.error("Error fetching dispute details:", error);
      message.warning("Could not fetch full details. Showing available information.");
    }
  };

  const handleUpdateNotes = async () => {
    if (!selectedDispute) return;
    
    try {
      const result = await dispatch(
        updateDisputeNotes({ disputeId: String(selectedDispute.id), notes: responseNotes })
      );
      if (updateDisputeNotes.fulfilled.match(result)) {
        message.success("Response notes updated successfully.");
        handleFetchDisputes();
        setIsNotesOpen(false);
      } else {
        const errorMessage = typeof result.payload === 'string' ? result.payload : "Failed to update notes. Please try again.";
        message.error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error updating notes:", error);
      message.error("Failed to update notes. Please try again.");
    }
  };

  const handleSubmitResponse = async () => {
    if (!selectedDispute) return;
    try {
      const result = await dispatch(
        submitDisputeResponse({ disputeId: String(selectedDispute.id), evidence: {} })
      );
      if (submitDisputeResponse.fulfilled.match(result)) {
        message.success("Dispute response submitted to Stripe successfully.");
        handleFetchDisputes();
        setIsDetailOpen(false);
      } else {
        const errorMessage = typeof result.payload === 'string' ? result.payload : "Failed to submit dispute response. Please try again.";
        message.error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error submitting dispute response:", error);
      message.error("Failed to submit dispute response. Please try again.");
    }
  };

  const getStatusBadge = (status?: string): React.ReactNode => {
    const color = getStatusColor(status);
    const label = getStatusLabel(status);
    return <Tag color={color}>{label}</Tag>;
  };

  const paginatedDisputes = filteredDisputes.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const themeMode: 'light' | 'dark' = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  return (
    <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
      <StyledThemeProvider theme={styledTheme}>
        <Container>
      <HeaderSection>
        <Title level={2} style={{ margin: 0 }}>
          Dispute Management
        </Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleFetchDisputes}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </HeaderSection>
      <StatsRow gutter={[12, 16]}>
        <Col xs={24} sm={12} md={8} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="total">
            <Statistic
              title="Total Disputes"
              value={stats.total}
              valueStyle={{ color: "#1890ff" }}
              prefix={<SafetyOutlined style={{ color: "#1890ff" }} />}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={8} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="needsResponse">
            <Statistic
              title="Needs Response"
              value={stats.needsResponse + stats.warningNeedsResponse}
              valueStyle={{ color: "#cf1322" }}
              prefix={<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={8} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="underReview">
            <Statistic
              title="Under Review"
              value={stats.underReview}
              valueStyle={{ color: "#13c2c2" }}
              prefix={<FileTextOutlined style={{ color: "#13c2c2" }} />}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={8} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="won">
            <Statistic
              title="Won"
              value={stats.won}
              valueStyle={{ color: "#52c41a" }}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={8} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="lost">
            <Statistic
              title="Lost"
              value={stats.lost}
              valueStyle={{ color: "#fa8c16" }}
              prefix={<CloseCircleOutlined style={{ color: "#fa8c16" }} />}
            />
          </StatCard>
        </Col>
      </StatsRow>
      <StyledCard>
        <TopControlBar>
          <CustomInput
            placeholder="Search by customer name..."
            value={searchQuery}
            onChange={(value: string) => setSearchQuery(value)}
            allowClear
            width="300px"
          />
          <CustomSelector
            value={statusFilter}
            onChange={(value: string) => setStatusFilter(value)}
            options={STATUS_FILTER_OPTIONS}
            width={200}
            height={36}
            borderRadius={8}
          />
        </TopControlBar>
        <DisputesTable
          disputes={paginatedDisputes}
          currentPage={currentPage}
          pageSize={pageSize}
          total={filteredDisputes.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          onViewDetails={handleViewDetails}
          getStatusBadge={getStatusBadge}
          isUrgent={isUrgentStatus}
          searchQuery={searchQuery}
          statusFilter={statusFilter}
          loading={loading}
        />
      </StyledCard>
      <DisputeDetailsModal
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          dispatch(clearSelectedDispute());
        }}
        selectedDispute={selectedDispute}
        isLoadingDetails={loadingDetails}
        responseNotes={responseNotes}
        isSubmitting={submittingResponse}
        onNotesOpen={() => setIsNotesOpen(true)}
        onSubmitResponse={handleSubmitResponse}
        getStatusBadge={getStatusBadge}
        isUrgent={isUrgentStatus}
        canSubmitResponse={canSubmitResponse}
      />

      <StyledModal
        title={
          <Space>
            <FontAwesomeIcon icon={faPen} fontSize={12}/>
            <span>Edit Response Notes</span>
          </Space>
        }
        open={isNotesOpen}
        onCancel={() => setIsNotesOpen(false)}
        destroyOnClose
        zIndex={2000}
        width={600}
        footer={
          <ModalFooter>
            <CloseButton onClick={() => setIsNotesOpen(false)}>
              Cancel
            </CloseButton>
            <SubmitButton
              type="primary"
              icon={<FontAwesomeIcon icon={faCheckCircle} fontSize={12}/>}
              onClick={handleUpdateNotes}
              loading={updatingNotes}
            >
              Save Notes
            </SubmitButton>
          </ModalFooter>
        }
      >
        <div>
          <Text strong>Response Notes</Text>
          <TextArea
            value={responseNotes}
            onChange={(e) => setResponseNotes(e.target.value)}
            placeholder="Enter notes for this dispute response. These notes will be included when submitting evidence to Stripe."
            rows={8}
            style={{ marginTop: 8 }}
          />
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
            These notes are stored locally and can be included when submitting evidence to Stripe.
          </Text>
        </div>
      </StyledModal>
        </Container>
      </StyledThemeProvider>
    </ConfigProvider>
  );
};

export default DisputeManagement;


